import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { Package } from '../types/packages'

interface PackageCardProps {
  pkg: Package
}

const LANGUAGE_COLORS: Record<string, string> = {
  go: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
  python: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  typescript: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  javascript: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
  rust: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  java: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  ruby: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  shell: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
}

function formatDownloads(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`
  return String(n)
}

export function PackageCard({ pkg }: PackageCardProps) {
  const [imgFailed, setImgFailed] = useState(false)
  const langColor = LANGUAGE_COLORS[pkg.language?.toLowerCase()] ?? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'

  const officialBorder = pkg.is_official
    ? 'border-orange-400 dark:border-orange-500 ring-1 ring-orange-400 dark:ring-orange-500'
    : 'border-gray-200 dark:border-gray-700'

  return (
    <Link
      to={`/packages/${pkg.slug}`}
      className={`group flex flex-col bg-white dark:bg-gray-900 border rounded-xl p-5 hover:border-primary-400 dark:hover:border-primary-500 hover:shadow-md transition-all duration-150 ${officialBorder}`}
    >
      {/* Icon + name row */}
      <div className="flex items-center gap-3 mb-3">
        <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-gray-100 dark:bg-gray-800 overflow-hidden flex items-center justify-center">
          {pkg.icon_url && !imgFailed ? (
            <img
              src={pkg.icon_url}
              alt={pkg.name}
              className="w-full h-full object-cover"
              onError={() => setImgFailed(true)}
            />
          ) : (
            <span className="text-xl font-bold text-gray-400 dark:text-gray-600 select-none uppercase">
              {pkg.slug[0]}
            </span>
          )}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-semibold text-gray-900 dark:text-gray-100 truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
              {pkg.slug}
            </span>
            {pkg.is_official && (
              <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 rounded-full">
                <svg className="w-3 h-3" viewBox="0 0 12 12" fill="currentColor">
                  <path d="M6 1l1.545 3.13 3.455.502-2.5 2.436.59 3.44L6 8.882 2.91 10.508l.59-3.44L1 4.632l3.455-.502L6 1z" />
                </svg>
                Official
              </span>
            )}
          </div>
          {pkg.latest_version && (
            <span className="text-xs text-gray-400 dark:text-gray-500">v{pkg.latest_version}</span>
          )}
          {pkg.author && (
            <span className="block text-xs text-gray-400 dark:text-gray-500">by {pkg.author}</span>
          )}
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 flex-1 mb-3">
        {pkg.description || 'No description provided.'}
      </p>

      {/* Footer row: language + downloads */}
      <div className="flex items-center justify-between gap-2 mt-auto">
        <div className="flex items-center gap-1.5 flex-wrap">
          {pkg.language && (
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${langColor}`}>
              {pkg.language}
            </span>
          )}
          {pkg.tags?.slice(0, 2).map((tag) => (
            <span key={tag} className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
              {tag}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          {formatDownloads(pkg.total_downloads)}
        </div>
      </div>
    </Link>
  )
}
